import { createProduct, type ProductInternalCodeSource } from '@/api/products';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useProductCategories } from '@/hooks/use-products';
import { useTranslation } from '@kgc/i18n';
import { PriceInput } from '@kgc/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MOCK_SUPPLIERS, VAT_RATES } from './mock-data';
import type { VatRate } from './types';

interface FormData {
  /** A kiválasztott kategória UUID-ja (`ProductCategory.id`) — ez megy a wire-re. */
  categoryId: string;
  sku: string;
  // ADR-129 Phase 1 — cikkszám-modell mezők
  internalCodeSource: ProductInternalCodeSource;
  manufacturerCode: string;
  barcode: string;
  name: string;
  shortName: string;
  description: string;
  brand: string;
  model: string;
  purchasePrice: string;
  sellingPriceNet: number | '';
  sellingPriceGross: number | '';
  vatRate: VatRate;
  minStockLevel: string;
  reorderQuantity: string;
  location: string;
  supplierId: string;
  // Rental fields
  rentalDailyRate: string;
  rentalWeeklyRate: string;
  rentalMonthlyRate: string;
  rentalDepositAmount: string;
}

export function ProductCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    categories,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useProductCategories();
  const [formData, setFormData] = useState<FormData>({
    categoryId: '',
    sku: '',
    internalCodeSource: 'AUTO_GENERATED',
    manufacturerCode: '',
    barcode: '',
    name: '',
    shortName: '',
    description: '',
    brand: '',
    model: '',
    purchasePrice: '',
    sellingPriceNet: '',
    sellingPriceGross: '',
    vatRate: 27,
    minStockLevel: '5',
    reorderQuantity: '10',
    location: '',
    supplierId: '',
    rentalDailyRate: '',
    rentalWeeklyRate: '',
    rentalMonthlyRate: '',
    rentalDepositAmount: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateMargin = () => {
    const purchase = parseFloat(formData.purchasePrice) || 0;
    const selling = formData.sellingPriceNet !== '' ? formData.sellingPriceNet : 0;
    if (purchase === 0) return 0;
    return (((selling - purchase) / purchase) * 100).toFixed(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert(t('product.nameRequired'));
      return;
    }

    // ADR-129 §2.2: csak SUPPLIER_FGS/SUPPLIER_MAV-nél kell manuális SKU.
    // AUTO_GENERATED esetén a backend foglalja le a `KGC-{YYYY}-{seq:04d}` kódot.
    if (formData.internalCodeSource !== 'AUTO_GENERATED' && !formData.sku.trim()) {
      alert(t('product.skuRequiredAlert'));
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await createProduct({
        // ADR-129: omit sku when AUTO_GENERATED to let backend auto-allocate.
        ...(formData.sku.trim() ? { sku: formData.sku.trim() } : {}),
        internalCodeSource: formData.internalCodeSource,
        ...(formData.manufacturerCode.trim()
          ? { manufacturerCode: formData.manufacturerCode.trim() }
          : {}),
        name: formData.name,
        shortName: formData.shortName || undefined,
        description: formData.description || undefined,
        // Q-T156: a választott kategória UUID-ja ténylegesen a wire-re megy.
        // (Korábban `undefined` volt »API would handle this« kommenttel — a BE
        // viszont sosem találta ki, minden új cikk kategória nélkül jött létre.)
        categoryId: formData.categoryId || undefined,
        brand: formData.brand || undefined,
        model: formData.model || undefined,
        barcode: formData.barcode || undefined,
        purchasePrice: parseFloat(formData.purchasePrice) || undefined,
        sellingPrice: formData.sellingPriceNet !== '' ? formData.sellingPriceNet : undefined,
        vatPercent: formData.vatRate,
        minStockLevel: parseInt(formData.minStockLevel) || undefined,
        reorderQuantity: parseInt(formData.reorderQuantity) || undefined,
      });

      navigate(`/products/${response.data.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.saveFailed');
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const selectedCategory = categories.find(c => c.id === formData.categoryId);
  // A bérlési díjblokk a kiválasztott kategórián múlik. A KGC élő kategória-fájában
  // (CAT-KISGEP / CAT-FURO / CAT-CSISZ / CAT-KERT / ACC-*) jelenleg NINCS bérgép-ág,
  // így a blokk nem jelenik meg — ha felvesznek egyet, magától működik.
  // ⚠️ A blokk mezői (napi/heti/havi díj, kaució) a `POST /products-direct` wire-jén
  // NEM utaznak (a bérgép külön `RentalEquipment` entitás) → külön tétel, lásd Q-T158.
  const isRentalEquipment = /BER|bérgép/i.test(
    `${selectedCategory?.code ?? ''} ${selectedCategory?.name ?? ''}`
  );

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/products')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('product.newProductTitle')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('product.newProductSubtitle')}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            className="bg-primary hover:bg-primary/90"
            disabled={isSaving}
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {saveError && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
            {saveError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category */}
          <Card>
            <CardHeader>
              <CardTitle>{t('common.category')}</CardTitle>
            </CardHeader>
            <CardContent>
              {categoriesLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
              ) : categoriesError ? (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {t('product.categoriesLoadError')}
                </p>
              ) : categories.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('product.noCategories')}
                </p>
              ) : (
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-label={t('common.category')}
                  data-testid="product-category-options"
                >
                  {categories.map(cat => {
                    const parent = cat.parentId
                      ? categories.find(c => c.id === cat.parentId)
                      : undefined;
                    const selected = formData.categoryId === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => updateField('categoryId', cat.id)}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600'
                        }`}
                      >
                        {parent ? `${parent.name} › ${cat.name}` : cat.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Basic info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.identifiers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                {/* ADR-129 Phase 1 — internalCodeSource selector */}
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.internalCodeSource.label')}
                  </label>
                  <select
                    className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    value={formData.internalCodeSource}
                    onChange={e =>
                      updateField('internalCodeSource', e.target.value as ProductInternalCodeSource)
                    }
                    data-testid="product-internal-code-source"
                  >
                    <option value="AUTO_GENERATED">
                      {t('product.internalCodeSource.AUTO_GENERATED')}
                    </option>
                    <option value="SUPPLIER_FGS">
                      {t('product.internalCodeSource.SUPPLIER_FGS')}
                    </option>
                    <option value="SUPPLIER_MAV">
                      {t('product.internalCodeSource.SUPPLIER_MAV')}
                    </option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {formData.internalCodeSource === 'AUTO_GENERATED'
                      ? t('product.skuOptional')
                      : t('product.skuRequired')}
                  </label>
                  <Input
                    value={formData.sku}
                    onChange={e => updateField('sku', e.target.value)}
                    placeholder={
                      formData.internalCodeSource === 'AUTO_GENERATED'
                        ? t('product.skuAutoPlaceholder')
                        : 'FGS-12345 / MAV-67890'
                    }
                    {...(formData.internalCodeSource !== 'AUTO_GENERATED'
                      ? { required: true }
                      : {})}
                    data-testid="product-sku-input"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.manufacturerCode')}
                  </label>
                  <Input
                    value={formData.manufacturerCode}
                    onChange={e => updateField('manufacturerCode', e.target.value)}
                    placeholder="LONCIN-G390"
                    data-testid="product-manufacturer-code"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.barcode')}
                  </label>
                  <Input
                    value={formData.barcode}
                    onChange={e => updateField('barcode', e.target.value)}
                    placeholder="0088381678940"
                  />
                </div>
                {/* Q-T159: a második, azonos feliratú „Gyártói cikkszám" mező eltávolítva.
                    A mögötte lévő `manufacturerPartNumber` oszlop SOSEM létezett a Product
                    táblán (a BE `as any`-vel írta) → kitöltve a mentés 400-zal elszállt.
                    A gyári cikkszámot az ADR-129-ratifikált `manufacturerCode` hordozza,
                    ami feljebb, „Gyártói cikkszám" felirattal már ott van. */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.locationCode')}
                  </label>
                  <Input
                    value={formData.location}
                    onChange={e => updateField('location', e.target.value)}
                    placeholder="A-02-03"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product details */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.productDetails')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="product-name"
                    className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t('product.productName')}
                  </label>
                  <Input
                    id="product-name"
                    value={formData.name}
                    onChange={e => updateField('name', e.target.value)}
                    placeholder={t('product.productNamePlaceholder')}
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.shortName')}
                  </label>
                  <Input
                    value={formData.shortName}
                    onChange={e => updateField('shortName', e.target.value)}
                    placeholder="DHP481RTJ"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.brand')}
                  </label>
                  <Input
                    value={formData.brand}
                    onChange={e => updateField('brand', e.target.value)}
                    placeholder="Makita"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.typeModel')}
                  </label>
                  <Input
                    value={formData.model}
                    onChange={e => updateField('model', e.target.value)}
                    placeholder="DHP481RTJ"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.supplier')}
                  </label>
                  <select
                    value={formData.supplierId}
                    onChange={e => updateField('supplierId', e.target.value)}
                    className="w-full rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('product.selectPlaceholder')}</option>
                    {MOCK_SUPPLIERS.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={e => updateField('description', e.target.value)}
                    className="w-full rounded-md border p-3 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                    rows={3}
                    placeholder={t('product.descriptionPlaceholder')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.pricing')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.purchasePriceNet')}
                  </label>
                  <Input
                    type="number"
                    value={formData.purchasePrice}
                    onChange={e => updateField('purchasePrice', e.target.value)}
                    placeholder="89000"
                    min={0}
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.vatRate')}
                    </label>
                    <select
                      value={formData.vatRate}
                      onChange={e => {
                        updateField('vatRate', parseInt(e.target.value) as VatRate);
                      }}
                      className="mt-1 w-40 rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                    >
                      {VAT_RATES.map(v => (
                        <option key={v.value} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <PriceInput
                    netValue={formData.sellingPriceNet}
                    grossValue={formData.sellingPriceGross}
                    vatRate={formData.vatRate}
                    netLabel={t('product.sellingPriceNet')}
                    grossLabel={t('product.sellingPriceGross')}
                    onNetChange={(net, gross) => {
                      setFormData(prev => ({
                        ...prev,
                        sellingPriceNet: net,
                        sellingPriceGross: gross,
                      }));
                    }}
                    onGrossChange={(gross, net) => {
                      setFormData(prev => ({
                        ...prev,
                        sellingPriceNet: net,
                        sellingPriceGross: gross,
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="mt-4 rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                <p
                  className="text-sm text-green-700 dark:text-green-300"
                  dangerouslySetInnerHTML={{
                    __html: t('product.calculatedMargin', { percent: String(calculateMargin()) }),
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Stock settings */}
          <Card>
            <CardHeader>
              <CardTitle>{t('product.stockSettings')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.minStockLevel')}
                  </label>
                  <Input
                    type="number"
                    value={formData.minStockLevel}
                    onChange={e => updateField('minStockLevel', e.target.value)}
                    min={0}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('product.reorderQuantity')}
                  </label>
                  <Input
                    type="number"
                    value={formData.reorderQuantity}
                    onChange={e => updateField('reorderQuantity', e.target.value)}
                    min={0}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rental settings */}
          {isRentalEquipment && (
            <Card>
              <CardHeader>
                <CardTitle>{t('product.rentalSettings')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.dailyRateFt')}
                    </label>
                    <Input
                      type="number"
                      value={formData.rentalDailyRate}
                      onChange={e => updateField('rentalDailyRate', e.target.value)}
                      placeholder="8500"
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.weeklyRateFt')}
                    </label>
                    <Input
                      type="number"
                      value={formData.rentalWeeklyRate}
                      onChange={e => updateField('rentalWeeklyRate', e.target.value)}
                      placeholder="45000"
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.monthlyRateFt')}
                    </label>
                    <Input
                      type="number"
                      value={formData.rentalMonthlyRate}
                      onChange={e => updateField('rentalMonthlyRate', e.target.value)}
                      placeholder="140000"
                      min={0}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('product.depositAmountFt')}
                    </label>
                    <Input
                      type="number"
                      value={formData.rentalDepositAmount}
                      onChange={e => updateField('rentalDepositAmount', e.target.value)}
                      placeholder="150000"
                      min={0}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/products')}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={isSaving}>
              {isSaving ? t('product.creating') : t('product.createProduct')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
